import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle, X, Star, Zap, Crown, Target, TrendingUp } from "lucide-react";

const Pricing = () => {
  const pricingPlans = [
    {
      name: "Pipeline Foundation",
      icon: Zap,
      price: "£1,500",
      period: "per month",
      description: "For growth-stage companies ready to build predictable demand generation infrastructure.",
      features: [
        "Full-funnel demand strategy",
        "LinkedIn SDR automation setup",
        "Account-based targeting (50 accounts)",
        "Lead scoring & qualification system",
        "CRM integration & pipeline tracking",
        "Monthly performance analytics",
        "Dedicated strategist"
      ],
      notIncluded: [
        "Multi-channel paid media",
        "Content syndication",
        "Executive positioning"
      ],
      popular: false,
      cta: "Build Your Pipeline"
    },
    {
      name: "Demand Accelerator",
      icon: Star,
      price: "£3,000",
      period: "per month",
      description: "Comprehensive B2B growth engine for companies serious about scaling revenue operations.",
      features: [
        "Everything in Pipeline Foundation",
        "Multi-channel paid media (LinkedIn, Google)",
        "Account-based marketing (150 accounts)",
        "Content syndication & amplification",
        "Landing page optimization",
        "Intent data integration",
        "Bi-weekly strategy calls",
        "Priority support",
        "Custom attribution dashboard"
      ],
      notIncluded: [
        "Executive thought leadership",
        "Event marketing support"
      ],
      popular: true,
      cta: "Accelerate Demand"
    },
    {
      name: "Enterprise Growth",
      icon: Crown,
      price: "£5,000+",
      period: "per month",
      description: "Full-stack demand generation for enterprise teams targeting aggressive revenue growth.",
      features: [
        "Everything in Demand Accelerator",
        "Unlimited account targeting",
        "Executive LinkedIn positioning",
        "Thought leadership content program",
        "Multi-touch ABM orchestration",
        "Sales enablement collateral",
        "Weekly executive strategy sessions",
        "24/7 dedicated support",
        "Full growth team (3+ specialists)",
        "Custom integrations & reporting",
        "Event & conference support"
      ],
      notIncluded: [],
      popular: false,
      cta: "Scale Enterprise Growth"
    }
  ];

  const addOns = [
    { name: "LinkedIn SDR Expansion", price: "£1,000/month", description: "Scale outbound with additional SDR capacity and account targeting", cta: "Add SDR Capacity" },
    { name: "Content & Thought Leadership", price: "£1,500/month", description: "Executive positioning with ghostwritten articles and LinkedIn content", cta: "Add Content Program" },
    { name: "Sales Enablement Package", price: "£1,000/month", description: "Battle cards, case studies, and collateral to accelerate deal velocity", cta: "Add Sales Enablement" }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-primary via-primary to-primary-light">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center text-white">
            <div className="inline-flex items-center gap-2 bg-secondary/20 px-6 py-3 rounded-full mb-6 border border-secondary/30">
              <Target className="w-5 h-5 text-secondary" />
              <span className="text-secondary font-semibold">Full Automation Systems</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              One Investment. <span className="text-secondary">Infinite Value.</span>
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-white/90 leading-relaxed">
              From £1k to £8k (average £3k) for complete automation systems that work for you 24/7, forever.
              Pay once for systems that never sleep, never take holidays, and generate leads around the clock.
            </p>
            <div className="flex flex-wrap gap-6 justify-center text-left">
              <div className="flex items-center gap-3">
                <TrendingUp className="w-6 h-6 text-secondary" />
                <div>
                  <p className="font-bold text-2xl">£1k - £8k</p>
                  <p className="text-white/80 text-sm">One-Time Investment</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <CheckCircle className="w-6 h-6 text-secondary" />
                <div>
                  <p className="font-bold text-2xl">24/7</p>
                  <p className="text-white/80 text-sm">Always Working</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Star className="w-6 h-6 text-secondary" />
                <div>
                  <p className="font-bold text-2xl">Forever</p>
                  <p className="text-white/80 text-sm">Yours To Keep</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Plans */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-6">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold mb-6 text-foreground">
                Let's Discuss Your <span className="text-secondary">Perfect Package</span>
              </h2>
              <p className="text-xl text-muted-foreground mb-4">
                Every business is different. We'll build the right automation system for your needs.
              </p>
              <p className="text-lg text-muted-foreground">
                Average investment: £3k • Systems work 24/7 forever • One-time payment, infinite value
              </p>
            </div>

            <div className="grid lg:grid-cols-3 gap-8">
              {pricingPlans.map((plan, index) => (
                <Card key={index} className={`relative border-2 transition-all duration-300 hover:shadow-luxury hover:-translate-y-2 h-full flex flex-col ${
                  plan.popular ? 'border-secondary shadow-glow scale-105' : 'border-transparent hover:border-secondary/20'
                }`}>
                  {plan.popular && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <div className="bg-secondary text-primary px-6 py-2 rounded-full text-sm font-semibold">
                        Most Popular
                      </div>
                    </div>
                  )}
                  
                  <CardHeader className="text-center pb-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-primary to-primary-light rounded-lg flex items-center justify-center mx-auto mb-4">
                      <plan.icon className="w-8 h-8 text-secondary" />
                    </div>
                    <CardTitle className="text-2xl font-bold text-foreground">{plan.name}</CardTitle>
                    <div className="mt-4">
                      <span className="text-4xl font-bold text-secondary">{plan.price}</span>
                      <span className="text-muted-foreground">/{plan.period}</span>
                    </div>
                    <p className="text-muted-foreground mt-4">{plan.description}</p>
                  </CardHeader>
                  
                  <CardContent className="flex-1 flex flex-col">
                    <div className="space-y-4 mb-8 flex-1">
                      {plan.features.map((feature, idx) => (
                        <div key={idx} className="flex items-center">
                          <CheckCircle className="w-5 h-5 text-secondary mr-3 flex-shrink-0" />
                          <span className="text-muted-foreground">{feature}</span>
                        </div>
                      ))}
                      {plan.notIncluded.map((feature, idx) => (
                        <div key={idx} className="flex items-center opacity-50">
                          <X className="w-5 h-5 text-muted-foreground mr-3 flex-shrink-0" />
                          <span className="text-muted-foreground">{feature}</span>
                        </div>
                      ))}
                    </div>
                    
                    <div className="mt-auto">
                      <Button 
                        variant={plan.popular ? "premium" : "default"} 
                        size="lg" 
                        className="w-full"
                        asChild
                      >
                        {plan.cta === "Build Your Pipeline" ? (
                          <a href="/pipeline-acceleration">
                            {plan.cta}
                          </a>
                        ) : plan.cta === "Accelerate Demand" ? (
                          <a href="/demand-generation">
                            {plan.cta}
                          </a>
                        ) : plan.cta === "Scale Enterprise Growth" ? (
                          <a href="/enterprise">
                            {plan.cta}
                          </a>
                        ) : (
                          <a href="/do-you-qualify">
                            {plan.cta}
                          </a>
                        )}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Growth Boosters Section */}
      <section className="py-20 bg-muted/50">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold mb-6 text-foreground">
                Scale Your Demand Engine — <span className="text-secondary">Growth Add-Ons</span>
              </h2>
              <p className="text-xl text-muted-foreground">
                Expand your pipeline capacity with these high-impact modules
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {addOns.map((addon, index) => (
                <Card key={index} className="border-2 border-secondary/20 shadow-elegant hover:shadow-luxury transition-all duration-300 hover:border-secondary/40">
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start mb-4">
                      <h3 className="text-xl font-semibold text-foreground">{addon.name}</h3>
                      <span className="text-secondary font-bold">{addon.price}</span>
                    </div>
                    <p className="text-muted-foreground mb-4">{addon.description}</p>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="w-full" 
                      asChild
                    >
                      <a href="/do-you-qualify">
                        {addon.cta}
                      </a>
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Soft CTA Section */}
      <section className="py-16 bg-gradient-to-r from-primary/5 to-secondary/5">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center">
            <h3 className="text-2xl md:text-3xl font-bold mb-4 text-foreground">
              Not Sure Which Package Fits Your Growth Stage?
            </h3>
            <p className="text-lg text-muted-foreground mb-8">
              Schedule a strategic review and we'll map the right solution to your revenue objectives
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button variant="premium" size="lg" asChild>
                <a href="/do-you-qualify">
                  Schedule Strategic Review
                </a>
              </Button>
              <Button variant="outline" size="lg" asChild>
                <a href="/do-you-qualify">
                  Get Custom Assessment
                </a>
              </Button>
            </div>
            <p className="text-sm text-muted-foreground mt-4">
              Free consultation • Custom pipeline analysis • No obligation
            </p>
          </div>
        </div>
      </section>


      <section className="py-20 bg-background">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-foreground">
              Our <span className="text-secondary">90-Day Guarantee</span>
            </h2>
            <p className="text-xl text-muted-foreground mb-12 leading-relaxed">
              If you don't see measurable results in 90 days, you don't pay until you do.
            </p>
            
            <div className="grid md:grid-cols-3 gap-8">
              <Card className="border-2 border-secondary/20 shadow-elegant">
                <CardContent className="p-8 text-center">
                  <h3 className="text-xl font-bold text-foreground mb-4">Month 1</h3>
                  <p className="text-muted-foreground">Infrastructure setup & first pipeline opportunities identified</p>
                </CardContent>
              </Card>
              <Card className="border-2 border-secondary/20 shadow-elegant">
                <CardContent className="p-8 text-center">
                  <h3 className="text-xl font-bold text-foreground mb-4">Month 2</h3>
                  <p className="text-muted-foreground">Campaign optimization & pipeline velocity acceleration</p>
                </CardContent>
              </Card>
              <Card className="border-2 border-secondary/20 shadow-elegant">
                <CardContent className="p-8 text-center">
                  <h3 className="text-xl font-bold text-foreground mb-4">Month 3</h3>
                  <p className="text-muted-foreground">Predictable demand engine delivering consistent qualified opportunities</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary">
        <div className="container mx-auto px-6 text-center">
          <div className="max-w-4xl mx-auto text-white">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Ready to Build Predictable Pipeline?
            </h2>
            <p className="text-xl mb-8 text-white/90">
              Schedule a strategic review and get a custom demand generation roadmap.
              90-day results guarantee included with every engagement.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button variant="premium" size="xl" asChild>
                <a href="/do-you-qualify">
                  Schedule Strategic Review
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Pricing;